import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  UserCheck,
  Loader2,
  MessageCircle,
  Phone,
  Building2,
  User,
  Clock,
  AlertCircle,
  ArrowRight,
  CheckCircle
} from "lucide-react";

export default function WhatsAppAsignacionesPage() {
  const [pendingConversations, setPendingConversations] = useState([]);
  const [assignedConversations, setAssignedConversations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const { getAuthHeaders, user } = useAuth();

  const [assignmentData, setAssignmentData] = useState({
    assigned_to: "",
    department_id: "",
    notes: ""
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [pendingRes, assignedRes, deptsRes, usersRes] = await Promise.all([
        axios.get(`${API}/whatsapp/assignments/pending`, getAuthHeaders()),
        axios.get(`${API}/whatsapp/conversations?status=assigned&limit=50`, getAuthHeaders()),
        axios.get(`${API}/departamentos`, getAuthHeaders()),
        axios.get(`${API}/users`, getAuthHeaders())
      ]);
      setPendingConversations(pendingRes.data?.conversations || []);
      setAssignedConversations(assignedRes.data?.conversations || []);
      setDepartments(deptsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAssignDialog = (conversation) => {
    setSelectedConversation(conversation);
    setAssignmentData({
      assigned_to: conversation.assigned_to || "",
      department_id: conversation.department_id || "",
      notes: ""
    });
    setShowDialog(true);
  };

  const handleAssign = async () => {
    if (!assignmentData.assigned_to && !assignmentData.department_id) {
      toast.error("Seleccione un usuario o departamento");
      return;
    }

    try {
      setAssigning(true);
      await axios.post(`${API}/whatsapp/assign`, {
        conversation_id: selectedConversation.id,
        assigned_to: assignmentData.assigned_to || null,
        department_id: assignmentData.department_id || null,
        notes: assignmentData.notes || null
      }, getAuthHeaders());
      
      toast.success("Conversación asignada exitosamente");
      setShowDialog(false);
      fetchData();
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al asignar");
    } finally {
      setAssigning(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString("es", { 
      day: "2-digit", 
      month: "short",
      hour: "2-digit", 
      minute: "2-digit" 
    });
  };

  const getInitials = (name, phone) => {
    if (name) {
      return name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();
    }
    return phone?.slice(-2) || "??";
  };

  const ConversationCard = ({ conv, showAssignButton = true }) => (
    <Card className="hover:shadow-md transition-shadow" data-testid={`conv-card-${conv.id}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="w-12 h-12">
              <AvatarFallback className="bg-green-100 text-green-700">
                {getInitials(conv.contact_name, conv.contact_phone)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white">
                {conv.contact_name || conv.contact_phone}
              </h3>
              {conv.contact_name && (
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {conv.contact_phone}
                </p>
              )}
            </div>
          </div>
          <div className="text-right">
            <Badge variant={conv.status === "new" ? "default" : "secondary"} className={conv.status === "new" ? "bg-amber-500" : ""}>
              {conv.status === "new" ? "Nuevo" : conv.status === "assigned" ? "Asignado" : conv.status}
            </Badge>
            <p className="text-xs text-slate-400 mt-1">{formatTime(conv.last_message_at)}</p>
          </div>
        </div>

        <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
            {conv.last_message}
          </p>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {conv.assigned_to_name && (
              <Badge variant="outline" className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {conv.assigned_to_name}
              </Badge>
            )}
            {conv.department_name && (
              <Badge variant="outline" className="flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                {conv.department_name}
              </Badge>
            )}
            {!conv.assigned_to_name && !conv.department_name && (
              <span className="text-sm text-slate-400">Sin asignar</span>
            )}
          </div>
          {showAssignButton && (
            <Button 
              size="sm" 
              onClick={() => openAssignDialog(conv)}
              className="bg-green-600 hover:bg-green-700"
            >
              <UserCheck className="w-4 h-4 mr-1" />
              {conv.assigned_to ? "Reasignar" : "Asignar"}
            </Button>
          )}
        </div>

        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
          <MessageCircle className="w-4 h-4" />
          <span>{conv.message_count} mensaje(s)</span>
          {conv.unread_count > 0 && (
            <>
              <span>•</span>
              <Badge className="bg-green-500 text-xs">{conv.unread_count} sin leer</Badge>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="whatsapp-asignaciones-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Asignaciones WhatsApp
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Asigna conversaciones a departamentos o usuarios
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Pending */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                  Pendientes de Asignación
                </h2>
                <Badge className="bg-amber-500">{pendingConversations.length}</Badge>
              </div>

              {pendingConversations.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-12 text-center">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-4" />
                    <p className="text-slate-500">No hay conversaciones pendientes</p>
                    <p className="text-sm text-slate-400">Todas las conversaciones están asignadas</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {pendingConversations.map((conv) => (
                    <ConversationCard key={conv.id} conv={conv} />
                  ))}
                </div>
              )}
            </div>

            {/* Assigned */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                  Asignadas Recientemente
                </h2>
                <Badge variant="secondary">{assignedConversations.length}</Badge>
              </div>

              {assignedConversations.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-12 text-center">
                    <UserCheck className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No hay conversaciones asignadas</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {assignedConversations.slice(0, 5).map((conv) => (
                    <ConversationCard key={conv.id} conv={conv} showAssignButton={true} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assignment Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Asignar Conversación</DialogTitle>
              <DialogDescription>
                Asigna esta conversación a un usuario o departamento
              </DialogDescription>
            </DialogHeader>

            {selectedConversation && (
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg mb-4">
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-green-100 text-green-700">
                      {getInitials(selectedConversation.contact_name, selectedConversation.contact_phone)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">
                      {selectedConversation.contact_name || selectedConversation.contact_phone}
                    </p>
                    <p className="text-sm text-slate-500">{selectedConversation.contact_phone}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label>Asignar a Usuario</Label>
                <Select
                  value={assignmentData.assigned_to}
                  onValueChange={(v) => setAssignmentData({...assignmentData, assigned_to: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar usuario..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Sin asignar</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name || u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Asignar a Departamento</Label>
                <Select
                  value={assignmentData.department_id}
                  onValueChange={(v) => setAssignmentData({...assignmentData, department_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar departamento..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Sin departamento</SelectItem>
                    {departments.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Notas (opcional)</Label>
                <Textarea
                  value={assignmentData.notes}
                  onChange={(e) => setAssignmentData({...assignmentData, notes: e.target.value})}
                  placeholder="Agregar notas sobre la asignación..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAssign} disabled={assigning} className="bg-green-600 hover:bg-green-700">
                {assigning && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Asignar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
